import { useState, useEffect, useCallback, Fragment } from 'react'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'

interface AuditLogEntry {
  id: number
  createdAt: string
  performedBy: string
  performedByName?: string
  actionType: string
  actionCategory: string
  customerId?: string
  customerName?: string
  customerTenantId?: string
  targetEntityType?: string
  targetEntityId?: string
  targetEntityName?: string
  actionDetails?: string
  previousValue?: string
  newValue?: string
  status: string
  errorMessage?: string
}

interface AuditLogsResponse {
  items: AuditLogEntry[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

interface FilterOptions {
  customers: Array<{ id: string; name: string }>
  performers: Array<{ email: string; name: string | null }>
  actionTypes: string[]
  actionCategories: string[]
}

// Format action type for display
function formatActionType(actionType: string): string {
  return actionType
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
}

// Get badge color based on action category
function getCategoryColor(category: string): string {
  switch (category) {
    case 'Authentication':
      return 'bg-blue-100 text-blue-800'
    case 'UserManagement':
      return 'bg-purple-100 text-purple-800'
    case 'Licensing':
      return 'bg-green-100 text-green-800'
    case 'MFA':
      return 'bg-orange-100 text-orange-800'
    case 'Integration':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

// Get status badge color
function getStatusColor(status: string): string {
  return status === 'Success'
    ? 'bg-green-100 text-green-800'
    : 'bg-red-100 text-red-800'
}

export default function AuditLogs() {
  const { getAccessToken } = useAuth()
  const [logs, setLogs] = useState<AuditLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalCount: 0,
    totalPages: 0,
  })

  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    customerId: '',
    performedBy: '',
    actionType: '',
    actionCategory: '',
  })
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    customers: [],
    performers: [],
    actionTypes: [],
    actionCategories: [],
  })

  // Expanded row for details
  const [expandedRow, setExpandedRow] = useState<number | null>(null)

  // Fetch filter options
  useEffect(() => {
    async function fetchFilters() {
      try {
        const token = await getAccessToken()
        const data = await api.getAuditLogFilters(token)
        setFilterOptions(data)
      } catch (err) {
        console.error('Failed to fetch filter options:', err)
      }
    }
    fetchFilters()
  }, [getAccessToken])

  // Fetch logs
  const fetchLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true)
      setError(null)
      const token = await getAccessToken()
      const data: AuditLogsResponse = await api.getAuditLogs(token, {
        ...filters,
        page,
        pageSize: pagination.pageSize,
      })
      setLogs(data.items || [])
      setPagination({
        page: data.page || 1,
        pageSize: data.pageSize || 25,
        totalCount: data.totalCount || 0,
        totalPages: data.totalPages || 0,
      })
    } catch (err) {
      console.error('Failed to fetch audit logs:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch audit logs')
    } finally {
      setLoading(false)
    }
  }, [getAccessToken, filters, pagination.pageSize])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  // Clear filters
  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      customerId: '',
      performedBy: '',
      actionType: '',
      actionCategory: '',
    })
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  // Safe JSON parse for display
  const safeJsonParse = (str: string | undefined): string => {
    if (!str) return ''
    try {
      return JSON.stringify(JSON.parse(str), null, 2)
    } catch {
      return str
    }
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Audit Log</h1>
          <p className="mt-2 text-sm text-gray-700">
            Track all changes made to customer Microsoft 365 tenants
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={e => handleFilterChange('startDate', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={e => handleFilterChange('endDate', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer
            </label>
            <select
              value={filters.customerId}
              onChange={e => handleFilterChange('customerId', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="">All Customers</option>
              {filterOptions.customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={filters.actionCategory}
              onChange={e => handleFilterChange('actionCategory', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="">All Categories</option>
              {filterOptions.actionCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Action Type
            </label>
            <select
              value={filters.actionType}
              onChange={e => handleFilterChange('actionType', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="">All Actions</option>
              {filterOptions.actionTypes.map(type => (
                <option key={type} value={type}>{formatActionType(type)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Performed By
            </label>
            <select
              value={filters.performedBy}
              onChange={e => handleFilterChange('performedBy', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="">All Users</option>
              {filterOptions.performers.map(p => (
                <option key={p.email} value={p.email}>{p.name || p.email}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={clearFilters}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading audit logs...</p>
        </div>
      ) : logs.length === 0 ? (
        /* Empty state */
        <div className="bg-white shadow rounded-lg">
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No audit logs found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {Object.values(filters).some(v => v)
                ? 'Try adjusting your filters to see more results.'
                : 'Logs will appear here once actions are performed on customer tenants.'}
            </p>
          </div>
        </div>
      ) : (
        /* Results table */
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-36 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="w-40 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performed By
                </th>
                <th className="w-36 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="w-36 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="w-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-16 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map(log => (
                <Fragment key={log.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-sm text-gray-500">
                      {formatDate(log.createdAt)}
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {log.performedByName || log.performedBy}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(log.actionCategory)}`}>
                        {log.actionCategory}
                      </span>
                      <div className="text-sm text-gray-900 mt-1 truncate">
                        {formatActionType(log.actionType)}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-900 truncate">
                      {log.customerName || '-'}
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-sm text-gray-900 truncate">{log.targetEntityName || '-'}</div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(log.status)}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-sm font-medium">
                      <button
                        onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                      >
                        {expandedRow === log.id ? 'Hide' : 'View'}
                      </button>
                    </td>
                  </tr>
                  {expandedRow === log.id && (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 bg-gray-50">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {log.actionDetails && (
                            <div>
                              <span className="font-medium text-gray-700">Details:</span>
                              <p className="text-gray-600 mt-1">{log.actionDetails}</p>
                            </div>
                          )}
                          {log.previousValue && (
                            <div>
                              <span className="font-medium text-gray-700">Previous Value:</span>
                              <pre className="text-gray-600 mt-1 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">
                                {safeJsonParse(log.previousValue)}
                              </pre>
                            </div>
                          )}
                          {log.newValue && (
                            <div>
                              <span className="font-medium text-gray-700">New Value:</span>
                              <pre className="text-gray-600 mt-1 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">
                                {safeJsonParse(log.newValue)}
                              </pre>
                            </div>
                          )}
                          {log.errorMessage && (
                            <div className="col-span-2">
                              <span className="font-medium text-red-700">Error:</span>
                              <p className="text-red-600 mt-1">{log.errorMessage}</p>
                            </div>
                          )}
                          {log.customerTenantId && (
                            <div>
                              <span className="font-medium text-gray-700">Customer Tenant ID:</span>
                              <p className="text-gray-600 mt-1 font-mono text-xs">{log.customerTenantId}</p>
                            </div>
                          )}
                          {log.targetEntityId && (
                            <div>
                              <span className="font-medium text-gray-700">Target Entity ID:</span>
                              <p className="text-gray-600 mt-1 font-mono text-xs">{log.targetEntityId}</p>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => fetchLogs(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchLogs(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                    <span className="font-medium">{pagination.totalPages}</span>
                    {' '}({pagination.totalCount} total results)
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => fetchLogs(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => fetchLogs(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
